import React, { useEffect, useState, useCallback } from "react";
import {
  ClipboardText, Plus, ArrowLeft, FloppyDisk, Trash, UserPlus, X,
  DownloadSimple, CheckCircle, Users, CalendarBlank, Info, UserCircle, ArrowSquareOut, Printer,
  TrendUp, TrendDown, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullPic = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);
const todayStr = () => new Date().toISOString().slice(0, 10);

// ---------------------------------------------------------------- Printable report cards
const escHtml = (v) => String(v == null ? "" : v).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const CARD_STYLES = `
  * { box-sizing: border-box; }
  html,body { margin:0; padding:0; background:#f4f5f7; color:#111827; font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; }
  .page { max-width: 760px; margin: 0 auto; background:#fff; padding:40px 44px; page-break-after: always; }
  .page:last-child { page-break-after: auto; }
  .top { display:flex; align-items:center; gap:14px; border-bottom:3px solid #111827; padding-bottom:16px; }
  .logo { width:52px; height:52px; object-fit:contain; }
  .brand { font-size:13px; letter-spacing:.14em; text-transform:uppercase; color:#374151; font-weight:700; }
  .doctitle { font-size:22px; font-weight:800; margin-top:2px; }
  .hero { display:flex; align-items:center; gap:18px; margin:26px 0 8px; }
  .avatar { width:72px; height:72px; border-radius:50%; background:#111827; color:#fff; display:flex; align-items:center; justify-content:center; font-size:28px; font-weight:800; overflow:hidden; }
  .avatar img { width:100%; height:100%; object-fit:cover; }
  .aname { font-size:26px; font-weight:800; line-height:1.1; }
  .aemail { color:#6b7280; font-size:13px; margin-top:3px; }
  .chip-role { display:inline-block; margin-top:6px; font-size:10px; letter-spacing:.12em; background:#eef2ff; color:#3730a3; padding:3px 8px; border-radius:4px; font-weight:700; }
  .stats { display:flex; gap:14px; margin:22px 0 10px; }
  .stat { flex:1; border:1px solid #e5e7eb; border-radius:8px; padding:14px 16px; }
  .stat .num { font-size:24px; font-weight:800; }
  .stat .lbl { font-size:10px; letter-spacing:.12em; color:#6b7280; text-transform:uppercase; margin-top:4px; }
  .section { font-size:11px; letter-spacing:.14em; color:#111827; text-transform:uppercase; font-weight:800; margin:26px 0 12px; border-bottom:1px solid #e5e7eb; padding-bottom:6px; }
  .session { border:1px solid #e5e7eb; border-radius:8px; padding:12px 14px; margin-bottom:10px; page-break-inside:avoid; }
  .srow { display:flex; justify-content:space-between; align-items:center; }
  .tname { font-weight:700; font-size:14px; }
  .badge { font-size:10px; letter-spacing:.08em; padding:2px 8px; border-radius:10px; font-weight:700; }
  .badge.final { background:#dcfce7; color:#166534; }
  .badge.draft { background:#fef3c7; color:#92400e; }
  .date { color:#6b7280; font-size:12px; margin-top:4px; }
  .chips { display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
  .chip { border:1px solid #e5e7eb; border-radius:6px; padding:3px 9px; font-size:12px; }
  .chip .k { color:#6b7280; margin-right:5px; }
  .chip .v { font-weight:700; }
  .muted { color:#9ca3af; font-size:12px; font-style:italic; margin-top:8px; }
  .foot { margin-top:30px; padding-top:14px; border-top:1px solid #e5e7eb; display:flex; justify-content:space-between; color:#9ca3af; font-size:11px; }
  .submeta { color:#6b7280; font-size:13px; margin:6px 0 18px; }
  .sumtable { width:100%; border-collapse:collapse; margin-top:4px; }
  .sumtable th, .sumtable td { text-align:left; padding:9px 10px; border-bottom:1px solid #e5e7eb; font-size:13px; }
  .sumtable th { font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:#6b7280; }
  .sumtable td.num { font-variant-numeric: tabular-nums; font-weight:700; }
  .sumtable .best { color:#166534; font-weight:700; }
  .sumtable .bestname { color:#6b7280; font-weight:400; font-size:11px; }
  .sumtable .na { color:#9ca3af; font-style:italic; font-weight:400; }
  .imp-good { color:#166534; }
  .imp-bad { color:#b91c1c; }
  .imp-flat { color:#6b7280; }
  @media print { body { background:#fff; } .page { padding:24px 28px; } @page { margin:12mm; } }
`;

const buildCardHtml = (data, academy) => {
  const logo = fullPic(academy?.logo_url);
  const pic = fullPic(data?.picture);
  const acName = escHtml(academy?.name || "Academy");
  const roleLabel = escHtml((data.primary_role || data.role || "athlete").replace(/_/g, " ").toUpperCase());
  const joined = data.added_at ? new Date(data.added_at).toLocaleDateString() : "—";
  const sessions = (data.test_history || []).map((h) => {
    const entries = Object.entries(h.metrics || {});
    const chips = entries.length
      ? `<div class="chips">${entries.map(([k, v]) => `<span class="chip"><span class="k">${escHtml(k)}</span><span class="v">${escHtml(v)}</span></span>`).join("")}</div>`
      : `<div class="muted">No results recorded for this session.</div>`;
    return `<div class="session">
      <div class="srow"><span class="tname">${escHtml(h.template_name)}</span><span class="badge ${h.status === "final" ? "final" : "draft"}">${escHtml((h.status || "draft").toUpperCase())}</span></div>
      <div class="date">${escHtml(h.test_date)}</div>${chips}
    </div>`;
  }).join("");
  const historyHtml = sessions || `<div class="muted">No recorded group-test results yet.</div>`;
  const trends = (data.metric_trends || []).filter((t) => t.count >= 2);
  const progressHtml = trends.length ? `
    <div class="section">Progress Over Time</div>
    <table class="sumtable">
      <thead><tr><th>Test</th><th>First</th><th>Latest</th><th>Change</th><th>Tests</th></tr></thead>
      <tbody>${trends.map((t) => {
        const arrow = t.direction === "flat" ? "&rarr;" : (t.direction === "down" ? "&#9660;" : "&#9650;");
        const cls = t.improved === true ? "imp-good" : (t.improved === false ? "imp-bad" : "imp-flat");
        const unit = t.unit ? ` ${escHtml(t.unit)}` : "";
        const sign = t.delta > 0 ? "+" : "";
        return `<tr><td>${escHtml(t.name)}</td><td class="num">${escHtml(_fmtNum(t.first.value))}${unit}</td><td class="num">${escHtml(_fmtNum(t.latest.value))}${unit}</td><td class="num ${cls}">${arrow} ${sign}${escHtml(_fmtNum(t.delta))}</td><td class="num">${t.count}</td></tr>`;
      }).join("")}</tbody>
    </table>` : "";
  return `<div class="page">
    <div class="top">
      ${logo ? `<img class="logo" src="${escHtml(logo)}" alt="logo"/>` : ""}
      <div><div class="brand">${acName}</div><div class="doctitle">Athlete Report Card</div></div>
    </div>
    <div class="hero">
      <div class="avatar">${pic ? `<img src="${escHtml(pic)}"/>` : escHtml((data.name || "A").charAt(0).toUpperCase())}</div>
      <div>
        <div class="aname">${escHtml(data.name)}</div>
        ${data.email ? `<div class="aemail">${escHtml(data.email)}</div>` : ""}
        <div class="chip-role">${roleLabel}</div>
      </div>
    </div>
    <div class="stats">
      <div class="stat"><div class="num">${escHtml(data.test_count)}</div><div class="lbl">Group tests</div></div>
      <div class="stat"><div class="num" style="font-size:15px;">${escHtml(joined)}</div><div class="lbl">Joined academy</div></div>
    </div>
    ${progressHtml}
    <div class="section">Test History</div>
    ${historyHtml}
    <div class="foot"><span>${acName}</span><span>Generated ${escHtml(new Date().toLocaleDateString())}</span></div>
  </div>`;
};

const _isTimeUnit = (unit) => {
  const u = String(unit || "").trim().toLowerCase();
  return u === "s" || u === "sec" || u === "secs" || u === "second" || u === "seconds" || u === "ms" || u === "min" || u === "mins" || u === "m:ss";
};

const _fmtNum = (n) => {
  const r = Math.round(n * 100) / 100;
  return String(r);
};

const buildSummaryHtml = (session, participants, results, academy) => {
  const logo = fullPic(academy?.logo_url);
  const acName = escHtml(academy?.name || "Academy");
  const items = session?.items || [];
  const partCount = (participants || []).length;
  const rows = items.map((it) => {
    const vals = [];
    (participants || []).forEach((p) => {
      const raw = results?.[p.pid]?.[it.name];
      const num = parseFloat(raw);
      if (raw != null && String(raw).trim() !== "" && !Number.isNaN(num)) vals.push({ num, name: p.name });
    });
    if (vals.length === 0) return `<tr><td>${escHtml(it.name)}</td><td>${escHtml(it.unit || "")}</td><td class="na">—</td><td class="na">No data</td><td class="na">No data</td></tr>`;
    const avg = vals.reduce((a, b) => a + b.num, 0) / vals.length;
    const lower = _isTimeUnit(it.unit);
    const best = vals.reduce((a, b) => ((lower ? b.num < a.num : b.num > a.num) ? b : a));
    return `<tr>
      <td>${escHtml(it.name)}</td>
      <td>${escHtml(it.unit || "")}</td>
      <td class="num">${vals.length}/${partCount}</td>
      <td class="num">${escHtml(_fmtNum(avg))}</td>
      <td class="num"><span class="best">${escHtml(_fmtNum(best.num))}</span> <span class="bestname">${escHtml(best.name)}</span></td>
    </tr>`;
  }).join("");
  return `<div class="page">
    <div class="top">
      ${logo ? `<img class="logo" src="${escHtml(logo)}" alt="logo"/>` : ""}
      <div><div class="brand">${acName}</div><div class="doctitle">Squad Summary</div></div>
    </div>
    <div class="submeta">${escHtml(session?.template_name || "Group Test")} &middot; ${escHtml(session?.test_date || "")}${session?.team_name ? ` &middot; ${escHtml(session.team_name)}` : ""} &middot; ${partCount} athletes</div>
    <div class="section">Team Averages &amp; Best Results</div>
    <table class="sumtable">
      <thead><tr><th>Test</th><th>Unit</th><th>Tested</th><th>Average</th><th>Best</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="foot"><span>${acName}</span><span>Generated ${escHtml(new Date().toLocaleDateString())}</span></div>
  </div>`;
};

const openPrintDoc = (cardsHtml, title) => {
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(title)}</title>
<style>${CARD_STYLES}</style></head>
<body>${cardsHtml}
<script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
</body></html>`;
  const w = window.open("", "_blank", "width=820,height=1040");
  if (!w) { toast.error("Allow pop-ups to print"); return false; }
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
};


const Field = ({ label, children }) => (
  <div>
    <label className="text-xs label-tiny text-text-secondary mb-1 block">{label}</label>
    {children}
  </div>
);

// ---------------------------------------------------------------- Create dialog
const NewSessionDialog = ({ open, onClose, academyId, athletes, coaches, onCreated }) => {
  const [templates, setTemplates] = useState([]);
  const [teams, setTeams] = useState([]);
  const [templateKey, setTemplateKey] = useState("");
  const [teamId, setTeamId] = useState("");
  const [coachId, setCoachId] = useState("");
  const [testDate, setTestDate] = useState(todayStr());
  const [checked, setChecked] = useState({});
  const [manual, setManual] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTestDate(todayStr());
    setManual("");
    setTeamId("");
    setCoachId("");
    // pre-check all academy athletes
    const init = {};
    (athletes || []).forEach((a) => { init[a.user_id] = true; });
    setChecked(init);
    Promise.all([
      api.get(`/academies/${academyId}/assessment-templates`),
      api.get(`/academies/${academyId}/teams`),
    ]).then(([tpl, tm]) => {
      const sel = tpl.data.selected || [];
      const all = tpl.data.templates || [];
      const usable = sel.length ? all.filter((t) => sel.includes(t.key)) : all;
      setTemplates(usable);
      setTemplateKey(usable[0]?.key || "");
      setTeams(tm.data || []);
    }).catch(() => toast.error("Could not load batteries"));
  }, [open, academyId, athletes]);

  const create = async () => {
    if (!templateKey) { toast.error("Pick a test battery"); return; }
    const participants = [];
    (athletes || []).forEach((a) => {
      if (checked[a.user_id]) participants.push({ athlete_id: a.user_id, name: a.name });
    });
    manual.split(/[\n,]/).map((s) => s.trim()).filter(Boolean).forEach((name) => {
      participants.push({ name });
    });
    if (participants.length === 0) { toast.error("Add at least one athlete"); return; }
    setCreating(true);
    try {
      const { data } = await api.post(`/academies/${academyId}/test-sessions`, {
        template_key: templateKey,
        test_date: testDate,
        team_id: teamId || null,
        assigned_coach_id: coachId || null,
        participants,
      });
      toast.success("Log sheet created");
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-surface border-white/10" data-testid="new-session-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">New group test log sheet</DialogTitle>
          <DialogDescription className="text-text-secondary text-sm">Pick a battery and the squad you are testing.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <Field label="TEST BATTERY">
            <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2.5 text-sm" data-testid="new-session-template">
              {templates.length === 0 && <option value="">No batteries available</option>}
              {templates.map((t) => <option key={t.key} value={t.key}>{t.name} · {t.items.length} tests</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="TEST DATE">
              <Input type="date" value={testDate} onChange={(e) => setTestDate(e.target.value)} data-testid="new-session-date" />
            </Field>
            <Field label="SQUAD / TEAM (OPTIONAL)">
              <select value={teamId} onChange={(e) => setTeamId(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded px-3 py-2.5 text-sm" data-testid="new-session-team">
                <option value="">— None —</option>
                {teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="SHARE RESULTS WITH A COACH (VIEW ONLY, OPTIONAL)">
            <select value={coachId} onChange={(e) => setCoachId(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2.5 text-sm" data-testid="new-session-coach">
              <option value="">— No coach —</option>
              {(coaches || []).map((c) => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label={`ATHLETES ON PLATFORM (${(athletes || []).length})`}>
            {(athletes || []).length === 0
              ? <div className="text-xs text-text-secondary">No athlete accounts yet — add names manually below.</div>
              : (
                <div className="max-h-40 overflow-y-auto border border-white/10 rounded divide-y divide-white/5" data-testid="new-session-athletes">
                  {athletes.map((a) => (
                    <label key={a.user_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-white/5">
                      <input type="checkbox" checked={!!checked[a.user_id]}
                        onChange={(e) => setChecked((c) => ({ ...c, [a.user_id]: e.target.checked }))}
                        data-testid={`new-session-athlete-${a.user_id}`} />
                      {a.name}
                    </label>
                  ))}
                </div>
              )}
          </Field>
          <Field label="ADD ATHLETES MANUALLY (one per line or comma-separated)">
            <Textarea value={manual} onChange={(e) => setManual(e.target.value)} rows={3}
              placeholder={"Aarav Shah\nMeera Nair"} data-testid="new-session-manual" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm label-tiny text-text-secondary hover:text-white" data-testid="new-session-cancel">CANCEL</button>
          <button onClick={create} disabled={creating}
            className="bg-brand-accent text-black px-5 py-2 text-sm label-tiny tracking-wider disabled:opacity-50 flex items-center gap-2" data-testid="new-session-create">
            <Plus size={15} weight="bold" /> {creating ? "CREATING..." : "CREATE SHEET"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------- Grid editor
// ---------------------------------------------------------------- Athlete profile drawer
const AthleteProfileDrawer = ({ academyId, academy, athlete, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!athlete?.user_id) return;
    setLoading(true);
    setData(null);
    api.get(`/academies/${academyId}/athletes/${athlete.user_id}/profile`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load athlete profile"))
      .finally(() => setLoading(false));
  }, [academyId, athlete]);

  const pic = fullPic(data?.picture);

  const printCard = () => {
    if (!data) return;
    openPrintDoc(buildCardHtml(data, academy), `${data.name} — Athlete Report Card`);
  };

  return (
    <Sheet open={!!athlete} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="bg-surface border-white/10 w-full sm:max-w-md overflow-y-auto" data-testid="athlete-profile-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading text-lg">Athlete profile</SheetTitle>
          <SheetDescription className="text-text-secondary text-sm">Academy profile and group-test history.</SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="text-text-secondary text-sm py-10 text-center">Loading…</div>
        ) : !data ? (
          <div className="text-text-secondary text-sm py-10 text-center">Profile unavailable.</div>
        ) : (
          <div className="space-y-6 mt-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-brand-accent/15 flex items-center justify-center overflow-hidden shrink-0">
                {pic ? <img src={pic} alt={data.name} className="w-full h-full object-cover" />
                     : <UserCircle size={40} weight="duotone" className="text-brand-accent" />}
              </div>
              <div className="min-w-0">
                <div className="font-heading text-xl truncate" data-testid="athlete-profile-name">{data.name}</div>
                {data.email && <div className="text-xs text-text-secondary truncate">{data.email}</div>}
                <div className="mt-1 inline-block label-tiny text-[9px] tracking-widest px-1.5 py-0.5 bg-white/10 text-text-secondary rounded">
                  {(data.primary_role || data.role || "athlete").replace(/_/g, " ").toUpperCase()}
                </div>
              </div>
            </div>

            <button
              onClick={printCard}
              className="w-full flex items-center justify-center gap-2 border border-brand-accent text-brand-accent px-4 py-2.5 text-xs label-tiny tracking-wider hover:bg-brand-accent/10 transition"
              data-testid="athlete-profile-print"
            >
              <Printer size={15} /> PRINT / SHARE CARD
            </button>

            <div className="grid grid-cols-2 gap-3">
              <div className="border border-white/10 rounded p-3">
                <div className="text-2xl font-heading tabular-nums text-brand-accent" data-testid="athlete-profile-test-count">{data.test_count}</div>
                <div className="label-tiny text-[10px] text-text-secondary mt-1">GROUP TESTS</div>
              </div>
              <div className="border border-white/10 rounded p-3">
                <div className="text-sm font-heading mt-1">{data.added_at ? new Date(data.added_at).toLocaleDateString() : "—"}</div>
                <div className="label-tiny text-[10px] text-text-secondary mt-1">JOINED</div>
              </div>
            </div>

            {(data.metric_trends || []).filter((t) => t.count >= 2).length > 0 && (
              <div data-testid="athlete-profile-trends">
                <div className="label-tiny text-[10px] text-brand-accent mb-2">// PROGRESS OVER TIME</div>
                <div className="space-y-2">
                  {data.metric_trends.filter((t) => t.count >= 2).map((t) => {
                    const good = t.improved === true, bad = t.improved === false;
                    const color = good ? "text-green-400" : bad ? "text-red-400" : "text-text-secondary";
                    const Icon = t.direction === "down" ? TrendDown : t.direction === "up" ? TrendUp : ArrowRight;
                    const sign = t.delta > 0 ? "+" : "";
                    return (
                      <div key={t.name} className="flex items-center justify-between border border-white/10 rounded px-3 py-2" data-testid={`athlete-trend-${t.name.replace(/\s+/g, '-')}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{t.name}</div>
                          <div className="text-[11px] text-text-secondary">{_fmtNum(t.first.value)} → {_fmtNum(t.latest.value)}{t.unit ? ` ${t.unit}` : ""} · {t.count} tests</div>
                        </div>
                        <div className={`flex items-center gap-1 text-sm font-heading tabular-nums shrink-0 ${color}`}>
                          <Icon size={15} weight="bold" /> {sign}{_fmtNum(t.delta)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <div className="label-tiny text-[10px] text-brand-accent mb-2">// TEST HISTORY</div>
              {data.test_history.length === 0 ? (
                <div className="text-sm text-text-secondary border border-dashed border-white/10 rounded p-4 text-center" data-testid="athlete-profile-history-empty">
                  No recorded group-test results yet.
                </div>
              ) : (
                <div className="space-y-3" data-testid="athlete-profile-history">
                  {data.test_history.map((h) => (
                    <div key={h.session_id} className="border border-white/10 rounded p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm">{h.template_name}</div>
                        <span className={`text-[10px] px-2 py-0.5 rounded label-tiny shrink-0 ${h.status === "final" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-300"}`}>{(h.status || "draft").toUpperCase()}</span>
                      </div>
                      <div className="text-xs text-text-secondary flex items-center gap-1.5 mt-1"><CalendarBlank size={12} /> {h.test_date}</div>
                      {Object.keys(h.metrics).length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {Object.entries(h.metrics).map(([k, v]) => (
                            <span key={k} className="text-[11px] bg-white/5 border border-white/10 rounded px-2 py-0.5">
                              <span className="text-text-secondary">{k}:</span> <span className="text-white">{v}</span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[11px] text-text-secondary mt-2 italic">No results recorded for this session.</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const LogSheet = ({ academyId, academy, sessionId, coaches, onBack, onChanged }) => {
  const [s, setS] = useState(null);
  const [results, setResults] = useState({});
  const [participants, setParticipants] = useState([]);
  const [notes, setNotes] = useState("");
  const [testDate, setTestDate] = useState("");
  const [assignedCoachId, setAssignedCoachId] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newName, setNewName] = useState("");
  const [profileAthlete, setProfileAthlete] = useState(null);
  const [printingSquad, setPrintingSquad] = useState(false);

  const load = useCallback(() => {
    api.get(`/academies/${academyId}/test-sessions/${sessionId}`).then(({ data }) => {
      setS(data);
      setResults(data.results || {});
      setParticipants(data.participants || []);
      setNotes(data.notes || "");
      setTestDate(data.test_date || "");
      setAssignedCoachId(data.assigned_coach_id || "");
      setDirty(false);
    }).catch(() => toast.error("Could not load log sheet"));
  }, [academyId, sessionId]);

  const changeCoach = async (cid) => {
    setAssignedCoachId(cid);
    try {
      const { data } = await api.patch(`/academies/${academyId}/test-sessions/${sessionId}`, { assigned_coach_id: cid || null });
      setS(data);
      toast.success(cid ? "Results shared with coach" : "Coach removed");
      onChanged && onChanged();
    } catch { toast.error("Could not update coach"); }
  };

  useEffect(() => { load(); }, [load]);

  const printSquad = async () => {
    const linked = (participants || []).filter((p) => p.athlete_id);
    if (linked.length === 0) {
      toast.info("No on-platform athletes in this sheet to print");
      return;
    }
    setPrintingSquad(true);
    try {
      const profiles = await Promise.all(
        linked.map((p) =>
          api.get(`/academies/${academyId}/athletes/${p.athlete_id}/profile`)
            .then((r) => r.data)
            .catch(() => null)
        )
      );
      const ok = profiles.filter(Boolean);
      if (ok.length === 0) { toast.error("Could not load athlete cards"); return; }
      const summary = buildSummaryHtml(s, participants, results, academy);
      const cards = ok.map((d) => buildCardHtml(d, academy)).join("");
      openPrintDoc(summary + cards, `${s?.template_name || "Squad"} — Squad Report Cards`);
      if (ok.length < linked.length) toast.info(`${linked.length - ok.length} card(s) could not be loaded`);
    } finally {
      setPrintingSquad(false);
    }
  };

  const setCell = (pid, item, val) => {
    setResults((r) => ({ ...r, [pid]: { ...(r[pid] || {}), [item]: val } }));
    setDirty(true);
  };
  const addRow = () => {
    const name = newName.trim();
    if (!name) return;
    const pid = `tmp-${Date.now()}`;
    setParticipants((p) => [...p, { pid, name }]);
    setNewName("");
    setDirty(true);
  };
  const removeRow = (pid) => {
    setParticipants((p) => p.filter((x) => x.pid !== pid));
    setResults((r) => { const n = { ...r }; delete n[pid]; return n; });
    setDirty(true);
  };

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      const body = { results, participants, notes, test_date: testDate };
      if (nextStatus) body.status = nextStatus;
      const { data } = await api.patch(`/academies/${academyId}/test-sessions/${sessionId}`, body);
      setS(data);
      setResults(data.results || {});
      setParticipants(data.participants || []);
      setDirty(false);
      toast.success(nextStatus === "final" ? "Marked final" : nextStatus === "draft" ? "Reopened" : "Saved");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const exportCsv = () => {
    if (!s) return;
    const head = ["Athlete", ...s.items.map((i) => `${i.name}${i.unit ? ` (${i.unit})` : ""}`)];
    const lines = [head.join(",")];
    participants.forEach((p) => {
      const row = [p.name, ...s.items.map((i) => (results[p.pid]?.[i.name] ?? "").toString().replace(/,/g, " "))];
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${s.template_name.replace(/\s+/g, "_")}_${s.test_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!s) return <div className="text-text-secondary text-sm py-10 text-center">Loading log sheet…</div>;

  const isFinal = s.status === "final";
  const canEdit = !!s.can_edit;
  const readOnly = isFinal || !canEdit;

  return (
    <div className="space-y-4" data-testid="log-sheet">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-text-secondary hover:text-white" data-testid="log-sheet-back"><ArrowLeft size={20} /></button>
          <div>
            <div className="font-heading text-xl flex items-center gap-2">
              {s.template_name}
              <span className={`text-[10px] px-2 py-0.5 rounded label-tiny ${isFinal ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-300"}`} data-testid="log-sheet-status">
                {isFinal ? "FINAL" : "DRAFT"}
              </span>
              {!canEdit && <span className="text-[10px] px-2 py-0.5 rounded label-tiny bg-blue-500/20 text-blue-300" data-testid="log-sheet-viewonly">VIEW ONLY</span>}
            </div>
            <div className="text-xs text-text-secondary flex items-center gap-3 mt-0.5">
              <span className="flex items-center gap-1"><Users size={13} /> {participants.length} athletes</span>
              {s.team_name && <span>· {s.team_name}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="border border-white/15 px-3 py-2 text-xs label-tiny flex items-center gap-2 hover:border-brand-accent" data-testid="log-sheet-export"><DownloadSimple size={14} /> CSV</button>
          <button onClick={printSquad} disabled={printingSquad} className="border border-white/15 px-3 py-2 text-xs label-tiny flex items-center gap-2 hover:border-brand-accent disabled:opacity-50" data-testid="log-sheet-print-squad"><Printer size={14} /> {printingSquad ? "PREPARING..." : "SQUAD CARDS"}</button>
          {canEdit && !isFinal && (
            <button onClick={() => save()} disabled={saving} className="border border-brand-accent text-brand-accent px-4 py-2 text-xs label-tiny flex items-center gap-2 hover:bg-brand-accent/10 disabled:opacity-50" data-testid="log-sheet-save">
              <FloppyDisk size={14} /> {saving ? "SAVING..." : "SAVE"}{dirty ? " *" : ""}
            </button>
          )}
          {canEdit && (isFinal
            ? <button onClick={() => save("draft")} disabled={saving} className="border border-white/15 px-4 py-2 text-xs label-tiny hover:border-white/30 disabled:opacity-50" data-testid="log-sheet-reopen">REOPEN</button>
            : <button onClick={() => save("final")} disabled={saving} className="bg-green-600 text-white px-4 py-2 text-xs label-tiny flex items-center gap-2 disabled:opacity-50" data-testid="log-sheet-finalize"><CheckCircle size={14} weight="fill" /> MARK FINAL</button>)}
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-end">
        <Field label="TEST DATE">
          <Input type="date" value={testDate} onChange={(e) => { setTestDate(e.target.value); setDirty(true); }} disabled={readOnly} className="w-44" data-testid="log-sheet-date" />
        </Field>
        {canEdit ? (
          <Field label="SHARED WITH COACH (VIEW ONLY)">
            <select value={assignedCoachId} onChange={(e) => changeCoach(e.target.value)}
              className="w-56 bg-white/5 border border-white/10 rounded px-3 py-2.5 text-sm" data-testid="log-sheet-coach">
              <option value="">— No coach —</option>
              {(coaches || []).map((c) => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
            </select>
          </Field>
        ) : s.assigned_coach_name ? (
          <Field label="SHARED WITH YOU BY ACADEMY">
            <div className="text-sm text-white py-2" data-testid="log-sheet-coach-readonly">{s.assigned_coach_name}</div>
          </Field>
        ) : null}
      </div>

      {/* Grid */}
      <div className="border border-white/10 rounded-lg overflow-x-auto" data-testid="log-sheet-grid">
        <table className="w-full border-collapse text-sm min-w-max">
          <thead>
            <tr className="bg-white/[0.04]">
              <th style={{ color: "#94a3b8" }} className="sticky left-0 z-10 bg-[#13161c] text-left px-3 py-2.5 label-tiny border-b border-r border-white/10 min-w-[160px]">ATHLETE</th>
              {s.items.map((i) => (
                <th key={i.name} className="px-3 py-2.5 text-left border-b border-white/10 min-w-[120px]" title={i.protocol || ""}>
                  <div className="label-tiny text-text-secondary flex items-center gap-1">
                    {i.name} {i.protocol && <Info size={11} className="opacity-50" />}
                  </div>
                  {i.unit && <div className="text-[10px] text-brand-accent normal-case">{i.unit}</div>}
                </th>
              ))}
              <th className="px-2 border-b border-white/10 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {participants.length === 0 && (
              <tr><td colSpan={s.items.length + 2} className="px-3 py-6 text-center text-text-secondary text-sm">No athletes. Add one below.</td></tr>
            )}
            {participants.map((p, ri) => (
              <tr key={p.pid} className="hover:bg-white/[0.02]" data-testid={`log-row-${ri}`}>
                <td style={{ color: "#ffffff" }} className="sticky left-0 z-10 bg-[#13161c] px-3 py-1.5 border-r border-b border-white/10 font-medium whitespace-nowrap">
                  {p.athlete_id ? (
                    <button
                      onClick={() => setProfileAthlete({ user_id: p.athlete_id, name: p.name })}
                      className="inline-flex items-center gap-1.5 hover:text-brand-accent transition group/athlete"
                      title="View academy profile"
                      data-testid={`log-athlete-link-${ri}`}
                    >
                      <span className="underline decoration-dotted decoration-white/30 underline-offset-2">{p.name}</span>
                      <ArrowSquareOut size={12} className="opacity-0 group-hover/athlete:opacity-100 shrink-0" />
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5" title="Off-platform entry — not a linked academy athlete">
                      {p.name}
                    </span>
                  )}
                </td>
                {s.items.map((i) => (
                  <td key={i.name} className="border-b border-white/5 px-1 py-1">
                    <input
                      value={results[p.pid]?.[i.name] ?? ""}
                      onChange={(e) => setCell(p.pid, i.name, e.target.value)}
                      disabled={readOnly}
                      className="w-full bg-transparent focus:bg-white/5 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-brand-accent disabled:opacity-70"
                      data-testid={`log-cell-${ri}-${i.name.replace(/\s+/g, '-')}`}
                      inputMode="decimal"
                    />
                  </td>
                ))}
                <td className="border-b border-white/5 text-center">
                  {!readOnly && <button onClick={() => removeRow(p.pid)} className="text-text-secondary hover:text-red-400" data-testid={`log-row-remove-${ri}`}><X size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="flex items-center gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRow()}
            placeholder="Add athlete name…" className="max-w-xs" data-testid="log-sheet-add-name" />
          <button onClick={addRow} className="border border-white/15 px-3 py-2 text-xs label-tiny flex items-center gap-2 hover:border-brand-accent" data-testid="log-sheet-add-row"><UserPlus size={14} /> ADD ROW</button>
        </div>
      )}

      <Field label="SESSION NOTES">
        <Textarea value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} disabled={readOnly} rows={2} placeholder="Conditions, equipment, observations…" data-testid="log-sheet-notes" />
      </Field>

      <AthleteProfileDrawer academyId={academyId} academy={academy} athlete={profileAthlete} onClose={() => setProfileAthlete(null)} />
    </div>
  );
};

// ---------------------------------------------------------------- Main tab
export const AcademyGroupTests = ({ academyId, academy, athletes, coaches = [], canManage = false }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/academies/${academyId}/test-sessions`).then(({ data }) => setSessions(data || []))
      .catch(() => toast.error("Could not load sessions"))
      .finally(() => setLoading(false));
  }, [academyId]);

  useEffect(() => { load(); }, [load]);

  const del = async (sid) => {
    if (!window.confirm("Delete this log sheet? This cannot be undone.")) return;
    try {
      await api.delete(`/academies/${academyId}/test-sessions/${sid}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (openId) {
    return (
      <LogSheet academyId={academyId} academy={academy} sessionId={openId} coaches={coaches}
        onBack={() => { setOpenId(null); load(); }}
        onChanged={load} />
    );
  }

  return (
    <div className="space-y-4" data-testid="academy-group-tests">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg">Group Test Log Sheets</h3>
          <p className="text-sm text-text-secondary">{canManage ? "Record a whole squad's battery results in one grid." : "Test results your academy has shared with you."}</p>
        </div>
        {canManage && (
          <button onClick={() => setNewOpen(true)} className="bg-brand-accent text-black px-5 py-2.5 text-sm label-tiny tracking-wider flex items-center gap-2" data-testid="group-tests-new">
            <Plus size={16} weight="bold" /> NEW LOG SHEET
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-text-secondary text-sm py-10 text-center">Loading…</div>
      ) : sessions.length === 0 ? (
        <div className="border border-white/10 rounded-lg p-10 text-center" data-testid="group-tests-empty">
          <ClipboardText size={40} weight="duotone" className="mx-auto text-brand-accent mb-3" />
          <div className="font-heading text-base mb-1">{canManage ? "No log sheets yet" : "Nothing shared with you yet"}</div>
          <p className="text-sm text-text-secondary mb-4">{canManage ? "Create your first group test to record a squad's results." : "When an admin shares a group test, it will appear here."}</p>
          {canManage && <button onClick={() => setNewOpen(true)} className="border border-brand-accent text-brand-accent px-5 py-2 text-xs label-tiny" data-testid="group-tests-empty-new">CREATE LOG SHEET</button>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((s) => (
            <div key={s.session_id} className="border border-white/10 rounded-lg p-4 bg-white/[0.02] hover:border-brand-accent/40 transition group" data-testid={`session-card-${s.session_id}`}>
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => setOpenId(s.session_id)} className="text-left flex-1" data-testid={`session-open-${s.session_id}`}>
                  <div className="font-heading text-base leading-tight">{s.template_name}</div>
                </button>
                <span className={`text-[10px] px-2 py-0.5 rounded label-tiny shrink-0 ${s.status === "final" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-300"}`}>{(s.status || "draft").toUpperCase()}</span>
              </div>
              <div className="text-xs text-text-secondary space-y-1 mt-3">
                <div className="flex items-center gap-1.5"><CalendarBlank size={13} /> {s.test_date}</div>
                <div className="flex items-center gap-1.5"><Users size={13} /> {s.participant_count} athletes · {s.item_count} tests</div>
                {s.team_name && <div className="truncate">Squad: {s.team_name}</div>}
                {s.assigned_coach_name && <div className="truncate flex items-center gap-1.5 text-blue-300" data-testid={`session-coach-${s.session_id}`}><UserPlus size={13} /> Shared: {s.assigned_coach_name}</div>}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button onClick={() => setOpenId(s.session_id)} className="flex-1 border border-white/15 px-3 py-1.5 text-xs label-tiny hover:border-brand-accent" data-testid={`session-edit-${s.session_id}`}>OPEN SHEET</button>
                {canManage && <button onClick={() => del(s.session_id)} className="text-text-secondary hover:text-red-400 px-2" data-testid={`session-delete-${s.session_id}`}><Trash size={16} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <NewSessionDialog open={newOpen} onClose={() => setNewOpen(false)} academyId={academyId} athletes={athletes} coaches={coaches}
        onCreated={(s) => { load(); setOpenId(s.session_id); }} />
    </div>
  );
};
